import React, { useState, FormEvent } from 'react';
import { lookupWordWithAI } from '../api/api';
import { AiDefinition } from '../types';

const DictionarySidebar: React.FC = () => {
    const [word, setWord] = useState('');
    const [result, setResult] = useState<AiDefinition | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e: FormEvent) => {
        e.preventDefault();
        if (!word.trim()) return;

        setIsLoading(true);
        setError('');
        setResult(null);

        try {
            const data = await lookupWordWithAI(word);
            setResult(data);
        } catch (err: any) {
            if (err.response && err.response.status === 400 && err.response.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Could not find the word or an error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="ai-dictionary-container">
            <h3>Vocabulary Lookup</h3>

            <form onSubmit={handleSearch} className="ai-search-form">
                <input
                    type="text"
                    value={word}
                    onChange={(e) => setWord(e.target.value)}
                    placeholder="e.g., chaotic"
                    disabled={isLoading}
                />
                <button type="submit" className="btn" disabled={isLoading}>
                    {isLoading ? '...' : 'Search'}
                </button>
            </form>

            {error && <p className="error-message">{error}</p>}

            {isLoading && <div className="loading-spinner"></div>}

            {result && (
                <div className="ai-result-card">
                    <div className="result-item">
                        <strong>1. Part of Speech:</strong>
                        <p>{result.partOfSpeech}</p>
                    </div>
                    <div className="result-item">
                        <strong>2. IPA:</strong>
                        <p className="ipa-text">{result.ipa}</p>
                    </div>
                    <div className="result-item">
                        <strong>3. Etymology:</strong>
                        <p>{result.etymology}</p>
                    </div>
                    <div className="result-item">
                        <strong>4. Definition:</strong>
                        <p>{result.definition}</p>
                    </div>
                    <div className="result-item">
                        <strong>5. Examples:</strong>
                        <ol className="example-list">
                            {result.examples.map((ex, index) => (
                                <li key={index}><em>"{ex}"</em></li>
                            ))}
                        </ol>
                    </div>
                    <div className="result-item">
                        <strong>6. Tip:</strong>
                        <p>{result.tip}</p>
                    </div>
                    {/* Mục mới cho Collocations */}
                    {result.collocations && result.collocations.length > 0 && (
                        <div className="result-item">
                            <strong>7. Collocations:</strong>
                            <div className="word-list-container">
                                {result.collocations.map(word => <span key={word} className="word-tag collocation-tag">{word}</span>)}
                            </div>
                        </div>
                    )}
                    {/* 7. Word Family */}
                    {result.wordFamily && result.wordFamily.length > 0 && (
                        <div className="result-item">
                            <strong>8. Word Family:</strong>
                            <div className="word-list-container">
                                {result.wordFamily.map(word => <span key={word} className="word-tag family-tag">{word}</span>)}
                            </div>
                        </div>
                    )}

                    {/* 8. Synonyms */}
                    {result.synonyms && result.synonyms.length > 0 && (
                        <div className="result-item">
                            <strong>9. Synonyms:</strong>
                            <div className="word-list-container">
                                {result.synonyms.map(word => <span key={word} className="word-tag synonym-tag">{word}</span>)}
                            </div>
                        </div>
                    )}

                    {/* 9. Antonyms */}
                    {result.antonyms && result.antonyms.length > 0 && (
                        <div className="result-item">
                            <strong>10. Antonyms:</strong>
                            <div className="word-list-container">
                                {result.antonyms.map(word => <span key={word} className="word-tag antonym-tag">{word}</span>)}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DictionarySidebar;